import { Injectable, OnDestroy } from '@angular/core';
import { TokenService } from '@morningstar/shared/auth';
import { environment } from '@morningstar/shared/environment';
import { select } from '@morningstar/shared/operators';
import { ClientService } from '@morningstar/shared/services/client';
import { InvoiceSelectors, InvoiceService } from '@morningstar/shared/services/cutoff';
import { LocationsService } from '@morningstar/shared/services/locations';
import { BehaviorSubject, combineLatest, EMPTY, merge, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, withLatestFrom, tap, shareReplay } from 'rxjs/operators';
import { StorageTypes } from './storage-types';

@Injectable({
  providedIn: 'root'
})
export class StorageService implements OnDestroy {

  // == ACTIONS =======================================================

  msClient = new BehaviorSubject<string>(null);
  msClient$ = this.msClient.asObservable();

  // == STATE =========================================================

  returnLinkSubject = new BehaviorSubject<string>(environment.bceLink);

  refreshClient = new BehaviorSubject(null);
  refreshClient$ = this.refreshClient.asObservable();

  invoiceServiceState$ = this.invoiceService.state$;
  cutoffDate$ = this.invoiceServiceState$.pipe(select<Date>(InvoiceSelectors.cutoffDate));

  pinMenu = new BehaviorSubject(this.getItem(StorageTypes.pinMenu)?.toLowerCase() == 'true');
  pinMenu$ = this.pinMenu.asObservable();

  location = new BehaviorSubject<string>('');
  location$ = this.location.asObservable().pipe(
    distinctUntilChanged()
  );

  locationList$ = this.location$.pipe(
    switchMap(location => {
      if (location) {
        return this.locationService.get(true);
      } else {
        return EMPTY;
      }
    })
  );

  locationName$ = combineLatest([this.location$, this.locationList$]).pipe(
    filter(([location, locationList]) => location && locationList.length > 0),
    map(([location, locationList]) => {
      const locationName = locationList.find(c => c.id === location);
      if (locationName) {
        return locationName.description;
      }
    })
  );

  client = new BehaviorSubject<string>(this.getItem(StorageTypes.client));
  client$ = this.client.asObservable().pipe(
    distinctUntilChanged()
  );

  futureClients = new BehaviorSubject<boolean>(this.getItem(StorageTypes.futureClients) === 'true');
  futureClients$ = this.futureClients.asObservable();

  theme = new BehaviorSubject<string>(this.getItem(StorageTypes.theme) || 'light-theme');
  theme$ = this.theme.asObservable();

  clientListIsActive = new BehaviorSubject<boolean>(
    this.getItem(StorageTypes.clientListIsActive) != null
        ? this.getItem(StorageTypes.clientListIsActive).toUpperCase() === 'TRUE'
        : true
  );
  clientListIsActive$ = this.clientListIsActive.asObservable();

  clientList$ = combineLatest([this.location$, this.refreshClient$, this.msClient$]).pipe(
    filter(([location, _, msClient]) => !!location),
    distinctUntilChanged(([location, refreshClient, msClient], [currLocation, currClient, currMSClient]) => {
      return location === currLocation &&
             refreshClient === currClient &&
             msClient === currMSClient;
    }),
    switchMap(([location, _, __]) => {
      return this.clientService.getClientsByLocation(location, true, true);
    }),
    shareReplay(1)
  );

  private incrementClient = new Subject();
  incrementClient$ = this.incrementClient.asObservable();

  onIncrementClient$ = this.incrementClient$.pipe(
    withLatestFrom(this.clientList$, this.client$),
    tap(([_, clientList, client]) => {
      const currentClientIndex = clientList.findIndex(c => c.id === client);
      let nextClientIndex = currentClientIndex + 1;
      if (currentClientIndex === clientList.length - 1) {
        nextClientIndex = 0;
      }
      this.setItem(StorageTypes.client, clientList[nextClientIndex].id);
    })
  );

  private decrementClient = new Subject();
  decrementClient$ = this.decrementClient.asObservable();

  onDecrementClient$ = this.decrementClient$.pipe(
    withLatestFrom(this.clientList$, this.client$),
    tap(([_, clientList, client]) => {
      const currentClientIndex = clientList.findIndex(c => c.id === client);
      let previousClientIndex = currentClientIndex - 1;
      if (currentClientIndex === 0) {
        previousClientIndex = clientList.length - 1;
      }
      this.setItem(StorageTypes.client, clientList[previousClientIndex].id);
    })
  );

  clientName$ = combineLatest([this.client$, this.clientList$]).pipe(
    filter(([client, clientList]) => clientList && clientList.length > 0),
    map(([client, clientList]) => {
      const clientName = clientList.find(c => c.id === client);
      if (clientName) {
        return clientName.name;
      }
    })
  );

  enabled = new BehaviorSubject<boolean>(true);
  enabled$ = this.enabled.asObservable();

  // == SUBSCRIPTION =========================================================

  private subscription = merge(
    this.cutoffDate$,
    this.locationList$,
    this.clientList$,
    this.clientListIsActive$,
    this.onIncrementClient$,
    this.onDecrementClient$,
    this.futureClients$
  ).subscribe();

  // == LIFE CYCLE ============================================================

  constructor(private tokenService: TokenService,
              private locationService: LocationsService,
              private clientService: ClientService,
              private invoiceService: InvoiceService) {
  }

  ngOnDestroy(){
    this.subscription.unsubscribe();
  }

  // == HELPERS ==============================================================

  clear() {
    this.msClient.next(this.tokenService.getMSClient());
  }

  setItem(storageType: StorageTypes, value: string) {

    localStorage.setItem(`${this.itemPrefix}-${storageType}`, value);

    if (storageType === StorageTypes.bceLink) {
      this.returnLinkSubject.next(value);
    } else if (storageType === StorageTypes.location) {
      this.location.next(value);
    } else if (storageType === StorageTypes.client){
      this.client.next(value);
    } else if (storageType === StorageTypes.theme){
      this.theme.next(value);
    } else if (storageType === StorageTypes.clientListIsActive) {
      this.clientListIsActive.next(value.toUpperCase() === 'TRUE');
    } else if (storageType === StorageTypes.pinMenu) {
      this.pinMenu.next(value?.toLowerCase() === 'true');
    } else if (storageType === StorageTypes.futureClients) {
      this.futureClients.next(value?.toLowerCase() === 'true');
    }
  }

  getItem(storageType: StorageTypes): string {
    return localStorage.getItem(`${this.itemPrefix}-${storageType}`);
  }

  removeItem(storageType: StorageTypes) {
    localStorage.removeItem(`${this.itemPrefix}-${storageType}`);
  }

  nextClient() {
    this.incrementClient.next();
  }

  previousClient() {
    this.decrementClient.next();
  }

  get itemPrefix(): string {
    const prefix = 'mstar-' + environment.name;

    const userId = this.tokenService.getUserId();

    return `${prefix}-${userId}`;
  }

  getFutureClient() : boolean {
    return this.futureClients.value === true;
  }
}
